import type { ReactNode } from "react";
import { CopyField } from "./CopyField.jsx";
import { longDate } from "../lib/format.js";

/**
 * The customer's link to the contract that was just issued.
 *
 * SHOWN ONCE, AND SAID SO. The server stores the token's SHA-256 and nothing
 * else, so this is not a value the screen can go back and fetch. Everything
 * here exists to get it somewhere safe before the page changes: it is
 * selectable, it has a Copy button that falls back to selecting by hand, and
 * the two ways it actually gets sent are one press each.
 *
 * THE SEND BUTTONS HAND OFF, THEY DO NOT SEND. `mailto:` and `wa.me` open the
 * installer's own mail client or WhatsApp with the message already written,
 * for them to read and send themselves. Nothing leaves this machine on their
 * behalf, which is the right default for a message to a customer and the
 * reason neither needs a confirmation.
 */
export function IssuedContractLink({
  link,
  onIssueAnother,
}: {
  readonly link: { readonly url: string; readonly expiresAt: string };
  readonly onIssueAnother: () => void;
}): ReactNode {
  const subject = "Your heat pump installation contract";
  const body =
    `Please read and sign your contract here:\n\n${link.url}\n\n` +
    `The link is yours alone and works until ${longDate(link.expiresAt)}.`;
  return (
    <div className="notice" role="status">
      <p>
        <strong>The contract is issued.</strong> This link is the
        customer&rsquo;s own and is shown here once — it is stored only as a
        fingerprint, so it cannot be read back off this screen later.
      </p>
      <CopyField
        label="The customer's link"
        value={link.url}
        copies="customer's contract link"
        hint={`Works until ${longDate(link.expiresAt)}. Anybody holding it can sign, so send it to the customer and nobody else.`}
      />
      <p className="row">
        <a className="cta" href={`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`}>
          Send by email
        </a>
        <a
          className="cta"
          href={`https://wa.me/?text=${encodeURIComponent(body)}`}
          target="_blank"
          rel="noreferrer noopener"
        >
          Send on WhatsApp
        </a>
        <a className="cta" href={link.url} target="_blank" rel="noreferrer noopener">
          Open it here to sign now
        </a>
      </p>
      <p className="small muted">
        <button type="button" className="link" onClick={onIssueAnother}>
          Issue another contract
        </button>{" "}
        — the one above is superseded by a variation rather than edited, so
        issuing again does not withdraw it.
      </p>
    </div>
  );
}

